/**
 * Normaliza o documento para a forma canônica usada ao enviar/armazenar/comparar:
 * remove tudo que não é alfanumérico (pontuação da máscara) e passa a maiúsculas.
 * Preserva letras — essencial para o CNPJ alfanumérico (IN RFB 2.229/2024).
 */
export function normalizeCnpj(value: string | null | undefined): string {
  return (value ?? '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

// Pesos do Módulo 11 para o cálculo dos dígitos verificadores do CNPJ.
const CNPJ_DV1_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_DV2_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Valida CNPJ nos dois formatos: numérico (legado) e alfanumérico (IN RFB 2.229/2024,
 * vigente a partir de julho/2026). As 12 primeiras posições aceitam A-Z e 0-9; os 2
 * dígitos verificadores continuam numéricos. O DV usa Módulo 11 com a conversão
 * `charCode - 48` (A=17, B=18, ..., Z=42), o que reproduz o cálculo numérico atual para
 * dígitos — garantindo retrocompatibilidade. Substitui `cnpj.isValid` da lib
 * `cpf-cnpj-validator`, que só trabalha com dígitos.
 *
 * Exemplo oficial da Receita Federal: 12.ABC.345/01DE-35
 */
export function isValidCnpjAlfa(value: string): boolean {
  if (!value) return false;

  const raw = value.replace(/[.\-/\s]/g, '').toUpperCase();
  // 12 posições alfanuméricas (A-Z, 0-9) + 2 dígitos verificadores numéricos.
  // Guard explícito: a função é exportada e pode ser reusada fora do input mascarado.
  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(raw)) return false;
  if (/^(.)\1+$/.test(raw)) return false;

  const charToNum = (c: string): number => c.charCodeAt(0) - 48;
  const calcDv = (slice: string, weights: number[]): number => {
    const sum = slice
      .split('')
      .reduce((acc, c, i) => acc + charToNum(c) * weights[i], 0);
    const rem = sum % 11;
    return rem < 2 ? 0 : 11 - rem;
  };

  const dv1 = calcDv(raw.slice(0, 12), CNPJ_DV1_WEIGHTS);
  if (dv1 !== parseInt(raw[12], 10)) return false;

  const dv2 = calcDv(raw.slice(0, 13), CNPJ_DV2_WEIGHTS);
  return dv2 === parseInt(raw[13], 10);
}
